import {useMemo} from 'react';

import {apiClient} from 'common/util/api-client';
import type {
  CohortSyncTime,
  CohortSyncTimeRequest,
  FeatureFlagApiPayload,
  FeatureFlagApiResponse,
  FeatureFlagHistoryData,
  FeatureFlagLimitsResults,
  FeatureFlagUsersRequest,
} from 'app_api/projects/feature_flags/types';
import {FlagType} from 'apps/feature-flags/src/constants';

interface UseFeatureFlagsApiProps {
  projectId: string | number;
  workspaceId?: string | number;
}

type ListFeatureFlagsResponse = FeatureFlagApiResponse[];
type FeatureFlagHistoryResponse = FeatureFlagHistoryData;
type GetFeatureFlagRequest = {id: string};
type GetFeatureFlagHistoryRequest = {id: string; page?: string};
type CreateFeatureFlagRequest = FeatureFlagApiPayload;
type UpdateFeatureFlagRequest = FeatureFlagApiPayload & {id: string};
type DeleteFeatureFlagRequest = {id: string};
type DuplicateFeatureFlagRequest = {id: string};
type ArchiveFeatureFlagRequest = {id: string};
type RestoreFeatureFlagRequest = {id: string};
type GetFeatureFlagUsersRequest = FeatureFlagUsersRequest;
type GetCohortSyncTimeRequest = CohortSyncTimeRequest;
type CopyFeatureFlagToProjectRequest = {id: string; dest_project: string; dest_workspace?: string};
type ListFeatureFlagEnvironmentsRequest = {id: string};
type UnlinkFeatureFlagEnvironmentsRequest = {id: string; target_project: string; target_workspace?: string};

type FeatureFlagUsersResults = {
  [k: string]: Array<{
    [k: string]: any;
  }>;
};

const DEFAULT_EXPERIMENT_VARIANT_NAME = `test`;
const DEFAULT_CONTROL_VARIANT_NAME = `control`;
const DEFAULT_ON_VARIANT_NAME = `On`;

export const DEFAULT_OFF_VARIANT_NAME = `Off`;
export const DEFAULT_DYNAMIC_CONFIG_VARIANT_NAME = `Config Key`;
export const DEFAULT_DYNAMIC_CONFIG_OFF_NAME = `$dynamic_config_disabled`;

export const DEVICE_ID = `device_id`;
export const DISTINCT_ID = `distinct_id`;

const DEFAULT_EXPERIMENT_VARIANTS = [
  {
    key: DEFAULT_CONTROL_VARIANT_NAME,
    value: DEFAULT_CONTROL_VARIANT_NAME,
    description: ``,
    split: 0.5,
    is_control: true,
    is_sticky: false,
  },
  {
    key: DEFAULT_EXPERIMENT_VARIANT_NAME,
    value: DEFAULT_EXPERIMENT_VARIANT_NAME,
    description: ``,
    split: 0.5,
    is_control: false,
    is_sticky: false,
  },
];

export const DEFAULT_FEATURE_GATE_VARIANTS = [
  {
    key: DEFAULT_ON_VARIANT_NAME,
    value: true,
    description: ``,
    split: 1,
    is_control: false,
    is_sticky: false,
  },
  {
    key: DEFAULT_OFF_VARIANT_NAME,
    value: false,
    description: ``,
    split: 0,
    is_control: true,
    is_sticky: false,
  },
];

const DEFAULT_DYNAMIC_CONFIG_VARIANTS = [
  {
    key: DEFAULT_DYNAMIC_CONFIG_VARIANT_NAME,
    value: {},
    description: ``,
    split: 1,
    is_control: false,
    is_sticky: false,
  },
  {
    key: DEFAULT_DYNAMIC_CONFIG_OFF_NAME,
    value: {},
    description: ``,
    split: 0,
    is_control: true,
    is_sticky: false,
  },
];

export const buildNewFeatureFlag = (flagType: FlagType): FeatureFlagApiPayload => {
  return {
    name: `New Feature Flag`,
    key: `new_feature_flag_${Date.now()}`,
    context: `distinct_id`,
    serving_method: `client`,
    ruleset: {
      variants: (() => {
        switch (flagType) {
          case FlagType.DYNAMIC_CONFIG:
            return DEFAULT_DYNAMIC_CONFIG_VARIANTS;
          case FlagType.EXPERIMENT:
            return DEFAULT_EXPERIMENT_VARIANTS;
          case FlagType.FEATURE_GATE:
          default:
            return DEFAULT_FEATURE_GATE_VARIANTS;
        }
      })(),
      rollout: [],
    },
    tags: [],
  };
};

export function featureFlagsApi(props: UseFeatureFlagsApiProps) {
  const basePath = props.workspaceId
    ? `/api/app/projects/${props.projectId}/workspaces/${props.workspaceId}/feature-flags`
    : `/api/app/projects/${props.projectId}/feature-flags`;
  return {
    listFeatureFlags: () => {
      // Always fetch all flags including archived - filtering is done client-side
      return apiClient.get<ListFeatureFlagsResponse>(`${basePath}?include_archived=true`);
    },

    getFeatureFlag: ({id}: GetFeatureFlagRequest) => {
      return apiClient.get<FeatureFlagApiResponse>(`${basePath}/${id}`);
    },

    getFeatureFlagHistory: ({id, page}: GetFeatureFlagHistoryRequest) => {
      const query = page ? `?page=` + page : ``;
      return apiClient.get<FeatureFlagHistoryResponse>(`${basePath}/${id}/history` + query);
    },

    createFeatureFlag: (flag: CreateFeatureFlagRequest) => {
      return apiClient.post<FeatureFlagApiResponse>(basePath, flag);
    },

    updateFeatureFlag: ({id, ...flag}: UpdateFeatureFlagRequest) => {
      return apiClient.put<FeatureFlagApiResponse>(`${basePath}/${id}`, flag);
    },

    deleteFeatureFlag: ({id}: DeleteFeatureFlagRequest) => {
      return apiClient.delete(`${basePath}/${id}`);
    },

    duplicateFeatureFlag: ({id}: DuplicateFeatureFlagRequest) => {
      return apiClient.post<FeatureFlagApiResponse>(`${basePath}/${id}/duplicate`, {});
    },

    archiveFeatureFlag: ({id}: ArchiveFeatureFlagRequest) => {
      return apiClient.post(`${basePath}/${id}/archive`, {});
    },

    restoreFeatureFlag: ({id}: RestoreFeatureFlagRequest) => {
      return apiClient.delete<FeatureFlagApiResponse>(`${basePath}/${id}/archive`);
    },

    getFeatureFlagLimits: () => {
      return apiClient.get<FeatureFlagLimitsResults>(`${basePath}/limits`);
    },

    getFeatureFlagUsers: (request: GetFeatureFlagUsersRequest, signal?: AbortSignal) => {
      return apiClient.post<FeatureFlagUsersResults>(`${basePath}/users`, request, {abortSignal: signal});
    },

    getCohortSyncTime: (request: GetCohortSyncTimeRequest) => {
      return apiClient.post<CohortSyncTime>(`${basePath}/last-cohort-sync-time`, request);
    },

    copyFeatureFlagToProject: ({id, ...params}: CopyFeatureFlagToProjectRequest) => {
      return apiClient.post<FeatureFlagApiResponse>(`${basePath}/${id}/copy_to_project`, params);
    },

    unlinkFeatureFlagEnvironments: ({id, ...params}: UnlinkFeatureFlagEnvironmentsRequest) => {
      const parentEntityPath = params.target_workspace
        ? `workspaces/${params.target_workspace}`
        : `projects/${params.target_project}`;
      const path = `/api/app/${parentEntityPath}/feature-flags`;
      return apiClient.post<FeatureFlagApiResponse>(`${path}/${id}/unlink_environments`, params);
    },

    listFeatureFlagEnvironments: ({id, ...params}: ListFeatureFlagEnvironmentsRequest) => {
      return apiClient.get<ListFeatureFlagsResponse>(`${basePath}/${id}/list_flag_environments`, params);
    },
  } as const;
}

export function useFeatureFlagsApi({projectId, workspaceId}: UseFeatureFlagsApiProps) {
  const api = useMemo(() => featureFlagsApi({projectId, workspaceId}), [projectId, workspaceId]);
  return api;
}
